// The builder on the bench page: the grid of cards, the rail that lists what has
// been picked, and the button that sends the lot. How far along the tier ladder
// the buyer is no longer lives here — that is the shared ladder store's job, and
// the builder hands it what it has staged so the bar counts it alongside the
// cart.

const initBundleConfigurator = ($el) => {
  const data = utils.JSONParse($el.querySelector("[data-bundle-configurator-data]")?.innerHTML) ?? {};
  const parent = data.parent;
  const ladder = window._sections.getBundleLadder();

  const catalog = [...(data.products ?? []), ...(data.accessories ?? [])];

  // What the thresholds are measured against is one list on the shop now, and
  // one rule reads it — the store's, so a staged line and a cart line can never
  // be counted by two different answers.
  const countsTowardThresholds = ladder.countsTowardThresholds;
  const countsTowardDiscount = ladder.countsTowardDiscount;

  const productsById = new Map();
  const rememberProduct = (item) => {
    if (item && !productsById.has(item.id)) productsById.set(item.id, item);
  };
  rememberProduct(parent);
  catalog.forEach((item) => {
    rememberProduct(item);
    (item.attachments ?? []).forEach(rememberProduct);
  });
  ladder.giftOptionsById.forEach(rememberProduct);

  const listVariants = (item) => item?.variants?.items ?? [];

  // An attachment is a product of its own, so it can be picked as a tier gift.
  // It only needs its card for placement, and to disappear with that card.
  const attachmentOwners = new Map();
  catalog.forEach((item) => {
    (item.attachments ?? []).forEach((attachment) => {
      attachmentOwners.set(attachment.id, [...(attachmentOwners.get(attachment.id) ?? []), item.id]);
    });
  });

  const text = ladder.text;
  const money = ladder.money;

  const state = window.Alpine.reactive({
    selected_ids: [],
    picked_variant_id: {},
    staged_gift_picks: {},
    released_gift_tiers: [],
    is_adding: false,
    added_to_cart: false,

    modal: {
      open: false,
      product_id: null,
      draft_variant_id: null,
      draft_attachments: [],
      was_selected: false,
    },

    retail_total: 0,
    percent_saving: 0,
    goods_saving: 0,
    total_saving: 0,
    total_saving_text: "",
    grand_total: 0,
    cta_retail_total: 0,
    cta_total: 0,
    free_ids: [],
    lines: [],
    deal_html: "",
    is_bare: true,
    cta_label: "",
    cta_disabled: true,
    get deal_card() {
      return buildDealCard();
    },
  });

  const getProduct = (id) => productsById.get(id);

  // One variant is nothing to choose between, so it ticks straight in and the
  // modal stays shut.
  const hasVariants = (id) => listVariants(getProduct(id)).length > 1;

  const getPickedVariant = (id) =>
    listVariants(getProduct(id)).find((variant) => variant.id === state.picked_variant_id[id]) ?? null;

  // The bench is bought in a colour, and which one is the product form's to
  // know. The builder watches it rather than keeping a second copy that can
  // drift, and re-reckons the rail when the buyer moves the swatch, because a
  // dearer finish has to move the total.
  let parentVariant = null;

  const syncParentVariant = (variant) => {
    if (!variant || parentVariant?.id === variant.id) return;
    parentVariant = variant;
    // Out of the watcher's own run, so what the reckoning reads does not become
    // a thing the watcher then listens for and answers with another reckoning.
    queueMicrotask(recalculate);
  };

  const priceOf = (id) => {
    const product = getProduct(id);
    if (!product) return 0;
    if (id === parent.id) return parentVariant?.price ?? product.price;
    if (hasVariants(id)) {
      const picked = getPickedVariant(id);
      return picked ? picked.price : product.lowest_price;
    }
    return product.price;
  };

  const compareOf = (id) => {
    const product = getProduct(id);
    if (!product) return 0;
    let priceSource = product;
    if (id === parent.id) priceSource = parentVariant ?? product;
    else if (hasVariants(id)) priceSource = getPickedVariant(id);
    const compareAtPrice = priceSource?.compare_at_price ?? 0;
    return compareAtPrice > priceOf(id) ? compareAtPrice : 0;
  };

  const cartLines = () => window._stores?.cart?.state?.items ?? [];

  const getStagedExtraIds = () => {
    const ids = [...state.selected_ids];
    Object.values(state.staged_gift_picks).forEach((id) => {
      if (id && !ids.includes(id)) ids.push(id);
    });
    return ids;
  };

  const getStagedIds = () => {
    const extraIds = getStagedExtraIds();
    if (hasFlaggedParentLine()) return extraIds;
    return [parent.id, ...extraIds];
  };

  const getButtonIds = () => {
    const stagedIds = getStagedIds();
    return stagedIds.length ? stagedIds : [parent.id];
  };

  const sumIds = (ids, keep) =>
    ids.filter(keep).reduce((sum, id) => sum + priceOf(id), 0);

  const sumStaged = (keep) => sumIds(getStagedIds(), keep);

  // What the builder adds to the ladder's sum. The cart side is the store's to
  // count, so only what is staged here travels.
  const getThresholdSubtotal = () => sumStaged(countsTowardThresholds);

  const getDiscountSubtotal = () => sumStaged(countsTowardDiscount);

  // A pick the buyer made here but has not sent yet. Whether the tier is open
  // and whether a bought gift already answers it are the store's to decide, so
  // this hands over the raw picks and lets it settle them.
  const getStagedGiftPicks = () => state.staged_gift_picks;

  const setStagedGiftPick = (tierIndex, productId) => {
    state.staged_gift_picks[tierIndex] = productId;
    state.released_gift_tiers = state.released_gift_tiers.filter((releasedTier) => releasedTier !== Number(tierIndex));
  };

  const getReleasedGiftTiers = () => state.released_gift_tiers;

  const keepReleasedGiftTiers = (reachedTierIndexes) => {
    const stillReached = state.released_gift_tiers.filter((releasedTier) => reachedTierIndexes.includes(releasedTier));
    if (stillReached.length !== state.released_gift_tiers.length) state.released_gift_tiers = stillReached;
  };

  const dropStagedGiftPicks = (tierIndexes) => {
    tierIndexes.forEach((tierIndex) => {
      state.staged_gift_picks[tierIndex] = null;
    });
  };

  const getStagedProducts = () => getStagedIds().map((id) => ({ id, price: priceOf(id) }));

  const getPercentSaving = () =>
    ladder.percentSavingFor(ladder.state.discount_base, ladder.state.discount_percentage);

  // What the buyer is charged for, cart and builder together. The cart is part of
  // the sum the tiers are read off, so it has to be part of the money the deal is
  // measured against too, or the rail would take a discount off less than it was
  // earned on.
  const getRetailTotal = () =>
    cartLines().reduce((sum, line) => sum + (line.original_line_price ?? 0), 0) +
    sumStaged(() => true);

  const getStagedGiftIds = () => {
    const stagedIds = getStagedIds();
    return Object.values(ladder.state.gift_picks ?? {})
      .filter((pick) => !pick.in_cart && stagedIds.includes(pick.id))
      .map((pick) => pick.id);
  };

  const getStagedRetailTotal = () => sumIds(getButtonIds(), () => true);

  const getStagedTotal = () => {
    const stagedGiftIds = getStagedGiftIds();
    const stagedGiftValue = stagedGiftIds.reduce((sum, id) => sum + priceOf(id), 0);
    const stagedGiftDiscountBase = stagedGiftIds
      .filter(countsTowardDiscount)
      .reduce((sum, id) => sum + priceOf(id), 0);
    const stagedPercentSaving = ladder.percentSavingFor(
      Math.max(sumIds(getButtonIds(), countsTowardDiscount) - stagedGiftDiscountBase, 0),
      ladder.state.discount_percentage
    );
    return getStagedRetailTotal() - stagedGiftValue - stagedPercentSaving;
  };

  const isSelected = (id) => state.selected_ids.includes(id);
  const isFree = (id) => state.free_ids.includes(id);

  // The rail lists what this builder is about to send, and nothing else. What is
  // already in the cart has its own list in the cart drawer; repeating it here
  // would turn the rail into a second, editable copy of the cart.
  const buildLines = () => {
    const freeIds = state.free_ids;
    const buttonIds = getButtonIds();
    const paidIds = buttonIds.filter((id) => !freeIds.includes(id));

    return [...paidIds, ...buttonIds.filter((id) => freeIds.includes(id))].map((id) => {
      const product = getProduct(id);
      const picked = id === parent.id ? parentVariant : getPickedVariant(id);
      return {
        id,
        title: product?.title ?? "",
        // The variant name under the product name, so a line reads as the thing
        // that was configured rather than as the product it started from.
        subtitle: picked?.title ?? "",
        price: priceOf(id),
        is_free: freeIds.includes(id),
        is_locked: id === parent.id,
        is_removable: id !== parent.id && (!freeIds.includes(id) || isSelected(id)),
      };
    });
  };

  const buildDealLabel = () => {
    const parts = ladder.buildDealParts();
    state.deal_html = ladder.renderDealHtml(parts);
    state.is_bare = !parts.length || state.total_saving <= 0;
  };

  const buildDealCard = () => ({
    is_active: !state.is_bare,
    is_bare: state.is_bare,
    label_html: state.deal_html,
    saving_text: state.total_saving_text,
    retail_text: money(state.retail_total),
    button_text: state.cta_label,
    full_price_text: money(state.cta_retail_total),
    net_price_text: money(state.cta_total),
    shows_full_price: !state.cta_disabled && state.cta_retail_total > state.cta_total,
    shows_net_price: !state.cta_disabled,
    is_disabled: state.cta_disabled || state.is_adding,
    is_loading: state.is_adding,
  });

  const buildCallToAction = () => {
    if (state.added_to_cart) {
      state.cta_label = text("cta_added");
      state.cta_disabled = true;
      return;
    }
    state.cta_label = text("cta_add");
    state.cta_disabled = getButtonIds().length === 0;
  };

  // What the page will cost if the button is pressed now, cart counted in. The
  // financing line reads it and nothing else does: Shop Pay splits the whole
  // order at checkout, so a line quoting the bench on its own would understate
  // every payment the buyer is about to make.
  const publishBuildTotal = () => {
    window._stores = window._stores || {};
    if (!window._stores.apexBuildTotal) {
      window._stores.apexBuildTotal = window.Alpine.reactive({ value: 0 });
    }
    window._stores.apexBuildTotal.value = state.grand_total;
  };

  const getStagedFreeIds = () =>
    Object.values(ladder.state.gift_picks ?? {})
      .filter((pick) => !pick.in_cart)
      .map((pick) => pick.id);

  // Called by the ladder store once it has worked the tiers out, so everything
  // the rail prints is read off the same figures the bar just drew. It must not
  // ask the store to recalculate again — that is the loop it sits inside.
  const onLadderChange = () => {
    state.free_ids = getStagedFreeIds();
    state.percent_saving = getPercentSaving();
    state.retail_total = getRetailTotal();
    state.goods_saving = state.percent_saving + ladder.state.gift_value;
    state.total_saving = ladder.state.total_saving;
    state.total_saving_text = ladder.state.total_saving_text;
    state.grand_total = state.retail_total - state.goods_saving;
    state.cta_retail_total = getStagedRetailTotal();
    state.cta_total = getStagedTotal();
    publishBuildTotal();
    state.lines = buildLines();
    buildDealLabel();
    buildCallToAction();
  };

  const recalculate = () => ladder.recalculate();

  const forgetStagedGiftPick = (id) => {
    Object.keys(state.staged_gift_picks).forEach((tierIndex) => {
      if (state.staged_gift_picks[tierIndex] === id) state.staged_gift_picks[tierIndex] = null;
    });
  };

  const releaseGiftTiersOf = (id) => {
    const releasedTiers = Object.keys(state.staged_gift_picks)
      .filter((tierIndex) => state.staged_gift_picks[tierIndex] === id)
      .map(Number);
    state.released_gift_tiers = [...new Set([...state.released_gift_tiers, ...releasedTiers])];
  };

  const clearStagedGiftPick = (id) => {
    releaseGiftTiersOf(id);
    forgetStagedGiftPick(id);
    recalculate();
  };

  const removeSelection = (id) => {
    const ownedAttachments = (getProduct(id)?.attachments ?? []).map((attachment) => attachment.id);
    state.selected_ids = state.selected_ids.filter(
      (selectedId) => selectedId !== id && !ownedAttachments.includes(selectedId)
    );
    delete state.picked_variant_id[id];
  };

  const toggleProduct = (id) => {
    if (id === parent.id) return;

    // Clicking a bonus gift that was never paid for releases the tier instead.
    if (isFree(id) && !isSelected(id)) {
      clearStagedGiftPick(id);
      return;
    }

    if (isSelected(id)) {
      removeSelection(id);
    } else {
      if (hasVariants(id)) {
        openVariants(id);
        return;
      }
      state.selected_ids.push(id);
    }
    recalculate();
  };

  const toggleAttachment = (attachmentId) => {
    if (isFree(attachmentId) && !isSelected(attachmentId)) {
      clearStagedGiftPick(attachmentId);
      return;
    }
    if (isSelected(attachmentId)) {
      state.selected_ids = state.selected_ids.filter((selectedId) => selectedId !== attachmentId);
    } else {
      state.selected_ids.push(attachmentId);
    }
    recalculate();
  };

  const openVariants = (id) => {
    const product = getProduct(id);
    const variants = listVariants(product);
    state.modal.product_id = id;
    state.modal.was_selected = isSelected(id);
    // A modal that opens on something sold out asks the buyer to undo a pick
    // they never made, so the first one that can be bought leads instead.
    state.modal.draft_variant_id =
      state.picked_variant_id[id] ??
      variants.find((variant) => variant.available)?.id ??
      variants[0]?.id ??
      null;
    state.modal.draft_attachments = (product?.attachments ?? [])
      .filter((attachment) => isSelected(attachment.id))
      .map((attachment) => attachment.id);
    state.modal.open = true;
  };

  const closeVariants = () => {
    state.modal.open = false;
    state.modal.product_id = null;
  };

  const setDraftVariant = (variantId) => {
    state.modal.draft_variant_id = variantId;
  };

  const getDraftVariant = () =>
    listVariants(getProduct(state.modal.product_id)).find(
      (variant) => variant.id === state.modal.draft_variant_id
    ) ?? null;

  const getDraftVariantList = () => listVariants(getProduct(state.modal.product_id));

  const toggleDraftAttachment = (attachmentId) => {
    const current = state.modal.draft_attachments;
    state.modal.draft_attachments = current.includes(attachmentId)
      ? current.filter((id) => id !== attachmentId)
      : [...current, attachmentId];
  };

  const sumDraftAttachments = () =>
    state.modal.draft_attachments.reduce(
      (total, attachmentId) => total + (getProduct(attachmentId)?.price ?? 0),
      0
    );

  const getDraftTotal = () => {
    const product = getProduct(state.modal.product_id);
    if (!product) return 0;
    const picked = getDraftVariant();
    return (picked ? picked.price : product.price) + sumDraftAttachments();
  };

  // The figure the shop asks for the same goods unconfigured. A variant with no
  // compare-at of its own counts at its own price, so the strike disappears
  // instead of printing the total twice.
  const getDraftCompareTotal = () => {
    const product = getProduct(state.modal.product_id);
    if (!product) return 0;
    const picked = getDraftVariant() ?? product;
    return (picked.compare_at_price || picked.price) + sumDraftAttachments();
  };

  const confirmVariants = () => {
    const id = state.modal.product_id;
    const product = getProduct(id);
    if (hasVariants(id) && !state.modal.draft_variant_id) return;

    state.picked_variant_id[id] = state.modal.draft_variant_id;
    if (!isSelected(id)) state.selected_ids.push(id);

    (product?.attachments ?? []).forEach((attachment) => {
      const wanted = state.modal.draft_attachments.includes(attachment.id);
      if (wanted && !isSelected(attachment.id)) state.selected_ids.push(attachment.id);
      if (!wanted && isSelected(attachment.id)) {
        state.selected_ids = state.selected_ids.filter((selectedId) => selectedId !== attachment.id);
      }
    });

    closeVariants();
    recalculate();
  };

  const removeFromVariants = () => {
    removeSelection(state.modal.product_id);
    closeVariants();
    recalculate();
  };

  // The clip plays in the theme's own gallery, the one the attachment tiles
  // open, so a YouTube link and an uploaded file take the same path.
  const openVideo = (id) => {
    const clip = getProduct(id)?.video;
    const gallery = window._stores?.videoGallery;
    if (!clip || !gallery) return;

    const onPhone = window.matchMedia("(max-width: 767px)").matches;
    const media = onPhone ? clip.mobile : clip.desktop;
    const isLandscape = !onPhone || clip.mobile_is_landscape;

    gallery.openGallery({
      media: [media],
      index: 0,
      autoplay: "true",
      loop: "true",
      controls: "true",
      muted: "false",
      use_desktop_ratio: isLandscape,
    });
  };

  // A widened card cannot keep the square ratio or it would tower over its row,
  // so the square a normal card resolves to is measured and handed to the CSS.
  //
  // It has to be measured again whenever the grid changes size, not once on the
  // way in. The stylesheet arrives as a link in the body, and a measurement
  // taken before it lands reads an unstyled card that is as wide as the row —
  // which is how a lone card on the last row ended up with an image the width of
  // the block and its name squeezed into a strip down the side.
  let lastMeasuredSide = null;

  const syncCardVisualHeight = () => {
    const grid = $el.querySelector(".grid");
    const normalCard = grid?.querySelector(".card:not(.span-half):not(.span-full) > .card-visual");
    if (!normalCard) return;
    const side = Math.round(normalCard.getBoundingClientRect().width);
    // The square only feeds the wide cards, so writing it cannot change what was
    // just measured. Skipping an unchanged value keeps the observer from
    // answering its own write all the same.
    if (side <= 0 || side === lastMeasuredSide) return;
    lastMeasuredSide = side;
    grid.style.setProperty("--card-visual-h", `${side}px`);
  };

  // TEMPORARY, FOR TESTING. See the note beside BUNDLE_FLAG_KEY in the ladder
  // store: every line the builder sends carries the flag, not just the bench,
  // because the ladder now counts carts the bench was never part of.
  const BUNDLE_FLAG_KEY = "_bundle";

  const hasFlaggedParentLine = () =>
    cartLines().some((line) => line.product_id === parent.id && line.properties?.[BUNDLE_FLAG_KEY]);

  // One variant per line, every one of them flagged. Everything else the function
  // works out from the cart. A flagged parent already in the cart has taken the
  // bench out of the staged list, so whatever is left here is what the buyer has
  // not bought yet.
  const isStagedGiftPick = (id) => Object.values(state.staged_gift_picks).includes(id);

  const buildLineProperties = (id) => {
    const giftProperties =
      isStagedGiftPick(id) && state.free_ids.includes(id) ? ladder.giftChoiceProperties(isSelected(id)) : {};
    return { [BUNDLE_FLAG_KEY]: "true", ...giftProperties };
  };

  const buildCartItems = () =>
    state.lines
      .map((line) => {
        const variantId =
          line.id === parent.id
            ? parentVariant?.id ?? parent.variant_id
            : getPickedVariant(line.id)?.id ??
              getProduct(line.id)?.variant_id ??
              ladder.giftOptionsById.get(line.id)?.variant_id;
        if (!variantId) return null;
        return { id: variantId, quantity: 1, properties: buildLineProperties(line.id) };
      })
      .filter(Boolean);

  const addToCart = async () => {
    if (state.is_adding || state.cta_disabled) return;
    state.is_adding = true;

    const response = await _cart.add({ items: buildCartItems() });
    state.is_adding = false;
    if (response?.cart_error) return;

    _stores.modal.setId("cart-drawer");
    state.selected_ids = [];
    state.picked_variant_id = {};
    state.staged_gift_picks = {};
    state.added_to_cart = true;
    recalculate();
    await utils.delay(3500);
    state.added_to_cart = false;
    recalculate();
  };

  const cardGrid = $el.querySelector(".grid");
  if (cardGrid && window.ResizeObserver) {
    new window.ResizeObserver(syncCardVisualHeight).observe(cardGrid);
  } else {
    window.addEventListener("resize", syncCardVisualHeight);
    requestAnimationFrame(syncCardVisualHeight);
  }

  // The root travels with it: the store outlives the page, and a builder whose
  // element has left the document is how it knows to stop asking this one.
  ladder.connectBuilder(
    {
      getThresholdSubtotal,
      getDiscountSubtotal,
      getStagedGiftPicks,
      getStagedProducts,
      setStagedGiftPick,
      dropStagedGiftPicks,
      forgetStagedGiftPick,
      getReleasedGiftTiers,
      keepReleasedGiftTiers,
      onLadderChange,
    },
    $el
  );

  return {
    bundle: state,
    // The modal markup is shared with the add-ons row on a product page, and
    // that row has no bundle of its own. Both hand it the same name.
    modal: state.modal,
    ladder: ladder.state,
    money,
    parent,
    priceOf,
    compareOf,
    isSelected,
    isFree,
    hasVariants,
    getProduct,
    getPickedVariant,
    getDraftVariant,
    getDraftVariantList,
    getDraftTotal,
    getDraftCompareTotal,
    syncParentVariant,
    toggleProduct,
    toggleAttachment,
    openVariants,
    closeVariants,
    setDraftVariant,
    toggleDraftAttachment,
    confirmVariants,
    removeFromVariants,
    openVideo,
    addToCart,
    recalculate,
  };
};

window._sections["initBundleConfigurator"] = initBundleConfigurator;
